import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, message } from 'antd';
import { useSelector } from 'react-redux';
import Modal from '@/components/modal/Modal';
import styles from './AiLabHelp.module.scss';

const MAX_LOG_LINES = 500;

const globalLogs = [];
const subscribers = new Set();

const formatArg = (arg) => {
  if (arg instanceof Error) {
    return `${arg.name}: ${arg.message}\n${arg.stack || ''}`;
  }
  try {
    return typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg);
  } catch {
    return '[unserializable]';
  }
};

const getCallerLocation = () => {
  const stack = new Error().stack;
  if (!stack) return '';

  const lines = stack.split('\n');
  for (const line of lines) {
    if (line.includes('AiLabHelp.jsx')) continue;

    const urlMatch = line.match(
      /(https?:\/\/[^\s:)]+|file:\/\/[^\s:)]+):(\d+):(\d+)/,
    );
    if (urlMatch) {
      const [, url, lineno] = urlMatch;
      try {
        const pathname = new URL(url).pathname;
        const basename = pathname.split('/').pop() || pathname;
        return `${basename}:${lineno}`;
      } catch {
        return `${url}:${lineno}`;
      }
    }

    const fileMatch = line.match(/(\/[^\s:)]+):(\d+):(\d+)/);
    if (fileMatch) {
      const basename = fileMatch[1].split('/').pop() || fileMatch[1];
      return `${basename}:${fileMatch[2]}`;
    }

    const fallbackMatch = line.match(/(?:at\s+)?([\w.\s<>]+?)(?:\s*[@(]|$)/);
    if (fallbackMatch) {
      const trimmed = fallbackMatch[1].trim();
      if (trimmed && trimmed !== 'Error') {
        return trimmed;
      }
    }
  }

  return '';
};

const pushLog = (level, args) => {
  const text = args.map(formatArg).join(' ');
  const location = getCallerLocation();
  globalLogs.push({
    time: new Date().toLocaleString('zh-CN'),
    level,
    text,
    location,
  });

  if (globalLogs.length > MAX_LOG_LINES) {
    globalLogs.splice(0, globalLogs.length - MAX_LOG_LINES);
  }

  subscribers.forEach((notify) => notify());
};

let loggerInstalled = false;

const installGlobalLogger = () => {
  if (loggerInstalled) return;
  loggerInstalled = true;

  // eslint-disable-next-line no-console
  const originalLog = console.log;
  // eslint-disable-next-line no-console
  const originalWarn = console.warn;
  // eslint-disable-next-line no-console
  const originalError = console.error;

  // eslint-disable-next-line no-console
  console.log = function logLog(...args) {
    pushLog('LOG', args);
    return originalLog.apply(this, args);
  };

  // eslint-disable-next-line no-console
  console.warn = function logWarn(...args) {
    pushLog('WARN', args);
    return originalWarn.apply(this, args);
  };

  // eslint-disable-next-line no-console
  console.error = function logError(...args) {
    pushLog('ERROR', args);
    return originalError.apply(this, args);
  };

  window.addEventListener('error', (event) => {
    pushLog('UNCAUGHT', [
      event.error ||
        new Error(event.message || 'Uncaught error', {
          cause: { filename: event.filename, lineno: event.lineno },
        }),
    ]);
  });

  window.addEventListener('unhandledrejection', (event) => {
    pushLog('UNHANDLED_REJECTION', [
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason)),
    ]);
  });
};

installGlobalLogger();

const AiLabHelp = () => {
  const location = useLocation();
  const uid = useSelector((state) => state.users.uid);
  const [visible, setVisible] = useState(false);
  const [version, setVersion] = useState(0);

  const isAiLabPage = useMemo(
    () => location.pathname.startsWith('/ailab'),
    [location.pathname],
  );

  useEffect(() => {
    if (!isAiLabPage) return undefined;

    const notify = () => setVersion((v) => v + 1);
    subscribers.add(notify);
    notify();

    return () => {
      subscribers.delete(notify);
    };
  }, [isAiLabPage]);

  const logText = useMemo(() => {
    const header = [
      '=== AI 体验日志 ===',
      `页面路径: ${location.pathname}`,
      `浏览器: ${navigator.userAgent}`,
      `用户ID: ${uid || '未登录'}`,
      `时间: ${new Date().toLocaleString('zh-CN')}`,
      '==================',
    ].join('\n');

    if (globalLogs.length === 0) {
      return `${header}\n暂无日志。`;
    }

    const body = globalLogs
      .map((log) => {
        const location = log.location ? ` [${log.location}]` : '';
        return `[${log.time}] [${log.level}]${location} ${log.text}`;
      })
      .join('\n');

    return `${header}\n${body}`;
  }, [location.pathname, uid, version]);

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(logText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = logText;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!success) throw new Error('execCommand copy failed');
      }
      message.success('日志已复制到剪贴板');
    } catch (error) {
      message.error('复制失败，请手动复制日志文本');
      // eslint-disable-next-line no-console
      console.error('复制日志失败:', error);
    }
  };

  if (!isAiLabPage) return null;

  return (
    <>
      <button
        className={styles.helpButton}
        onClick={() => setVisible(true)}
        type="button"
        aria-label="帮助"
      >
        ?
      </button>
      <Modal
        open={visible}
        onCancel={() => setVisible(false)}
        title="AI 体验异常？"
        showFooter={false}
        width={420}
      >
        <div className={styles.modalBody}>
          <p className={styles.tip}>
            如果遇到 AI
            体验异常，请点击“复制日志”，我们会将浏览器控制台的所有日志一并复制，方便你发送给客服排查。
          </p>
          <div className={styles.actions}>
            <Button onClick={() => setVisible(false)}>关闭</Button>
            <Button type="primary" onClick={handleCopy}>
              复制日志
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AiLabHelp;
